import axios from "axios";
import { encryptPayload, decryptPayload } from "../utils/EncryptionDecryption";
import API_ENDPOINTS from "./apiEndpoints";

const BASE_URL = process.env.REACT_APP_API_BASE_URL || "/api";
const SUBSCRIPTION_KEY = process.env.REACT_APP_APIM_KEY;

const apiManager = axios.create({
  baseURL: BASE_URL,
});

apiManager.interceptors.request.use(
  (config) => {
    config.headers["Ocp-Apim-Subscription-Key"] = SUBSCRIPTION_KEY;

    const token = localStorage.getItem("accessToken");
    const isRefresh = config.url === API_ENDPOINTS.REFRESH_TOKEN;
    if (!isRefresh && token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    } else if (isRefresh && config.headers && config.headers["Authorization"]) {
      delete config.headers["Authorization"];
    }
    if (config.data instanceof FormData) {
      return config;
    }
    try {
      const contentType = (
        config.headers["Content-Type"] ||
        config.headers["content-type"] ||
        ""
      ).toLowerCase();
      const alreadyEncrypted =
        typeof config.data === "string" &&
        contentType === "text/plain" &&
        config.headers["X-Encrypted"] === "1";

      if (!alreadyEncrypted) {
        config.data = encryptPayload(config.data);
        config.headers = config.headers || {};
        config.headers["Content-Type"] = "text/plain";
        config.headers["X-Encrypted"] = "1";
      }
    } catch (err) {
      return Promise.reject(new Error("Encryption failed"));
    }
    return config;
  },
  (error) => Promise.reject(error),
);

apiManager.interceptors.response.use(
  (response) => {
    if (response.data) {
      try {
        const decrypted = decryptPayload(response.data);
        response.data = JSON.parse(decrypted);
      } catch (err) {
        return Promise.reject(new Error("Decryption failed"));
      }
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const hadAuthHeader = !!originalRequest.headers?.["Authorization"];
    if (error.response?.status === 401 && !originalRequest._retry && hadAuthHeader) {
      originalRequest._retry = true;
      const refreshToken = localStorage.getItem("refreshToken");
      if (!refreshToken) {
        localStorage.clear();
        window.location.href = "/";
        return Promise.reject(error);
      }
      try {
        const refreshResponse = await apiManager.post(
          API_ENDPOINTS.REFRESH_TOKEN,
          { refreshToken },
        );
        const data = refreshResponse.data;
        localStorage.setItem("accessToken", data.accessToken);
        localStorage.setItem("refreshToken", data.refreshToken);
        originalRequest.headers = originalRequest.headers || {};
        if (originalRequest.data && typeof originalRequest.data === "string") {
          originalRequest.headers["X-Encrypted"] = "1";
          originalRequest.headers["Content-Type"] = "text/plain";
        }
        originalRequest.headers["Authorization"] = `Bearer ${data.accessToken}`;
        return apiManager(originalRequest);
      } catch (refreshError) {
        localStorage.clear();
        window.location.href = "/";
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

export default apiManager;